import { Modal, Typography } from "antd";
import React, { useState } from "react";
import { PDFDocument } from "pdf-lib";
import dayjs from "dayjs";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
// import { formImageBase64 } from "../Form/FormRegImg";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const DownloadModal = ({ open, handleClose, title, res }) => {
  const [loading, setLoading] = useState(false);

  const getImageBase64 = async (filePath) => {
    const response = await fetch(filePath);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const splitAddressByWord = (text = "", maxLength = 30) => {
    if (text.length <= maxLength) return [text, ""];
    const words = text.split(" ");
    let line1 = "";
    let line2 = "";
    for (const word of words) {
      if ((line1 + word).length <= maxLength) {
        line1 += (line1 ? " " : "") + word;
      } else {
        line2 += (line2 ? " " : "") + word;
      }
    }
    return [line1, line2];
  };

  const convertCoordsToPosition = (xPx, yPx) => {
    const scaleX = 0.24;
    const scaleY = 0.2261;
    return { x: xPx * scaleX, y: yPx * scaleY };
  };

  const adjustY = (yPt, fontSize, referenceFontSize = 10) => {
    const delta = referenceFontSize - fontSize;
    const adjustPerPoint = 1.5;
    return yPt + delta * adjustPerPoint;
  };

  const importImgFromDoc = async (docList = [], mergedPdf) => {
    if (docList.length === 0) return;

    for (const doc of docList) {
      const imageUrl = doc.azure_url;
      try {
        const imageRes = await fetch(imageUrl);
        const imageBuffer = await imageRes.arrayBuffer();
        const ext = imageUrl.split(".").pop().toLowerCase();

        let imageEmbed;
        if (ext === "jpg" || ext === "jpeg" || ext === "jfif") {
          imageEmbed = await mergedPdf.embedJpg(imageBuffer);
        } else if (ext === "png") {
          imageEmbed = await mergedPdf.embedPng(imageBuffer);
        } else {
          console.warn(`Unsupported image format: ${ext}`);
          continue;
        }

        const imageDims = imageEmbed.scale(1);
        const A4_WIDTH = 595;
        const A4_HEIGHT = 842;

        const scale = Math.min(
          1,
          A4_WIDTH / imageDims.width,
          A4_HEIGHT / imageDims.height,
        );

        const scaledWidth = imageDims.width * scale;
        const scaledHeight = imageDims.height * scale;

        const x = (A4_WIDTH - scaledWidth) / 2;
        const y = (A4_HEIGHT - scaledHeight) / 2;

        const page = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);
        page.drawImage(imageEmbed, {
          x,
          y,
          width: scaledWidth,
          height: scaledHeight,
        });
      } catch (err) {
        console.error(`Error loading image from ${imageUrl}:`, err);
      }
    }
  };

  const _HandleSubmit = async () => {
    setLoading(true);

    try {
      // 0. load img
      const formImageBase64 = await getImageBase64("/pdf/BR02_0001.jpg");
      // 1. โหลดฟอนต์
      const fontBuffer = await fetch("/fonts/Prompt-Regular.ttf").then((res) =>
        res.arrayBuffer(),
      );
      const arrayBufferToBase64 = (buffer) => {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
      };

      const base64Font = arrayBufferToBase64(fontBuffer);

      pdfMake.vfs = {
        "Prompt-Regular.ttf": base64Font,
      };

      pdfMake.fonts = {
        Prompt: {
          normal: "Prompt-Regular.ttf",
          bold: "Prompt-Regular.ttf",
          italics: "Prompt-Regular.ttf",
          bolditalics: "Prompt-Regular.ttf",
        },
      };

      const makeText = (x, y, value, align = "left") => ({
        absolutePosition: { x, y: adjustY(y, fontSize) },
        text: value,
        alignment: align,
      });

      const makeEllipse = (x, y) => ({
        canvas: [
          {
            type: "ellipse",
            x,
            y: adjustY(y, fontSize),
            r1: 15,
            r2: 4.5,
            lineColor: "#000000",
            lineWidth: 1,
          },
        ],
      });

      const makeCheck = (x, y) => ({
        absolutePosition: { x, y: adjustY(y, fontSize) },
        canvas: [
          {
            type: "polyline",
            lineColor: "#000000",
            lineWidth: 1.5,
            points: [
              { x: 0, y: 5 },
              { x: 3, y: 10 },
              { x: 10, y: 0 },
            ],
          },
        ],
      });

      const withCoords = (xPx, yPx, fn) => {
        const { x, y } = convertCoordsToPosition(xPx, yPx);
        return fn(x, y);
      };

      const measureCtx = document.createElement("canvas").getContext("2d");
      measureCtx.font = "8pt Prompt";

      const splitTextByWidth = (text = "", maxWidth) => {
        const words = text.split(" ");
        let current = "";

        for (const word of words) {
          const testLine = current ? `${current} ${word}` : word;

          const width = measureCtx.measureText(testLine).width;

          if (width <= maxWidth) {
            current = testLine;
          } else {
            break;
          }
        }
        return current;
      };

      const drawTextLines = (text = "", rows = []) => {
        let remainingText = text.trim();
        const result = [];
        rows.forEach((row) => {
          if (!remainingText) return;

          const line = splitTextByWidth(remainingText, row.maxWidth);

          result.push(withCoords(row.x, row.y, (x, y) => makeText(x, y, line)));

          remainingText = remainingText.slice(line.length).trim();
        });
        return result;
      };

      const fontSize = 8; // 9

      // TODO : the commented code sections can be deleted after merging to pre-prod
      // 2. สร้าง PDF หน้าแรก (ฐานจาก JPG + text ทับ)
      const docDefinition = {
        pageSize: "A4",
        pageMargins: [0, 0, 0, 0],
        background: [{ image: formImageBase64, width: 595, height: 842 }],
        defaultStyle: {
          font: "Prompt",
          fontSize: fontSize,
        },
        content: [
          // profile
          withCoords(1650, 55, (x, y) =>
            makeText(
              x,
              y,
              "วันที่สมัคร : " +
                dayjs(res.data.profile.register_date).format("DD/MM/YYYY"),
              "center",
            ),
          ),
          withCoords(1650, 140, (x, y) =>
            makeText(x, y, res.data.profile.member_code, "center"),
          ),
          withCoords(120, 645, (x, y) =>
            makeText(
              x,
              y,
              res.data.profile.id_card || res.data.profile.passport_id,
            ),
          ),
          // (() => {
          //   const prefixMap = { Mr: 337, Mrs: 438, Miss: 570 };
          //   const prefix = res.data.profile.prefix;
          //   if (prefixMap[prefix]) {
          //     const { x, y } = convertCoordsToPosition(prefixMap[prefix], 717);
          //     return makeEllipse(x, y);
          //   } else {
          //     const { x, y } = convertCoordsToPosition(935, 797);
          //     return makeText(x, y, prefix);
          //   }
          // })(),
          (() => {
            // Mr: 150, Mrs: 320 , Miss: 496
            const prefixMap = { Mr: 150, Mrs: 320, Miss: 496 };
            const prefix = res.data.profile.prefix;

            if (prefixMap[prefix]) {
              const { x, y } = convertCoordsToPosition(prefixMap[prefix], 789);
              return makeCheck(x, y); // เปลี่ยนจาก makeEllipse เป็น makeCheck
            } else {
              const { x: checkX, y: checkY } = convertCoordsToPosition(
                712,
                789,
              );
              const { x: textX, y: textY } = convertCoordsToPosition(935, 797);

              return [
                makeCheck(checkX, checkY),
                makeText(textX, textY, prefix),
              ];
            }
          })(),

          withCoords(120, 964, (x, y) =>
            makeText(x, y, res.data.profile.full_name_th),
          ),
          withCoords(120, 1107, (x, y) =>
            makeText(x, y, res.data.profile.full_name_en),
          ),
          // ...[
          //   ...(res.data.profile.birthDay?.toString().padStart(2, "0") || ""),
          // ].map((char, i) => {
          //   const { x, y } = convertCoordsToPosition(-1812 + i * 102, 980);
          //   return makeText(x, y, char, "center");
          // }),
          // ...[
          //   ...(res.data.profile.birthMonth?.toString().padStart(2, "0") || ""),
          // ].map((char, i) => {
          //   const { x, y } = convertCoordsToPosition(-1510 + i * 105, 980);
          //   return makeText(x, y, char, "center");
          // }),
          // ...[...(res.data.profile.birthYear?.toString() || "")].map(
          //   (char, i) => {
          //     const { x, y } = convertCoordsToPosition(-1210 + i * 115, 980);
          //     return makeText(x, y, char, "center");
          //   },
          // ),
          withCoords(210, 1248, (x, y) =>
            makeText(
              x,
              y,
              res.data.profile.birthDay +
                " / " +
                res.data.profile.birthMonth +
                " / " +
                res.data.profile.birthYear,
            ),
          ),
          withCoords(645, 1248, (x, y) => makeText(x, y, res.data.profile.age)),
          withCoords(440, 1332, (x, y) =>
            makeText(x, y, res.data.profile?.nationality_text),
          ),

          // (() => {
          //   const email = res.data.profile.email;
          //   const firstLine = email.slice(0, 25);
          //   const secondLine = email.length > 25 ? email.slice(25) : "";
          //   return [
          //     withCoords(172, 1409, (x, y) => makeText(x, y, firstLine)),
          //     ...(secondLine
          //       ? [withCoords(172, 1450, (x, y) => makeText(x, y, secondLine))]
          //       : []),
          //   ];
          // })(),
          withCoords(365, 1773, (x, y) =>
            makeText(x, y, res.data.profile.email),
          ),
          withCoords(380, 1843, (x, y) =>
            makeText(x, y, res.data.profile.mobile),
          ),

          // address_profile
          // ...splitAddressByWord(res.data.address_profile?.address, 50).map(
          //   (line, idx) => {
          //     const coords = idx === 0 ? [425, 1494] : [90, 1586];
          //     return withCoords(...coords, (x, y) => makeText(x, y, line));
          //   },
          // ),
          // ...splitAddressByWord(res.data.address_profile?.address_text, 20).map(
          //   (line, idx) => {
          //     const coords = idx === 0 ? [770, 1940] : [120, 2012];
          //     return withCoords(...coords, (x, y) => makeText(x, y, line));
          //   },
          // ),

          // address_profile
          // ...drawTextLines(res.data.address_profile?.address_text ?? "", [
          //   { x: 770, y: 1938, maxLength: 25 }, // line 1
          //   { x: 120, y: 2012, maxLength: 70 }, // line 2
          //   { x: 120, y: 2080, maxLength: 100 }, // line 3
          // ]),
          ...drawTextLines(res.data.address_profile?.address_text ?? "", [
            { x: 770, y: 1938, maxWidth: 130 }, // line 1
            { x: 120, y: 2012, maxWidth: 320 }, // line 2
            { x: 120, y: 2080, maxWidth: 1000 }, // line 3
          ]),
          // withCoords(272, 1682, (x, y) =>
          //   makeText(x, y, res.data.address_profile?.sub_district),
          // ),
          // withCoords(830, 1682, (x, y) =>
          //   makeText(x, y, res.data.address_profile?.district),
          // ),
          // withCoords(220, 1775, (x, y) =>
          //   makeText(x, y, res.data.address_profile?.province),
          // ),
          // withCoords(915, 1775, (x, y) =>
          //   makeText(x, y, res.data.address_profile?.post_code),
          // ),

          // address_mailing
          (() => {
            const isPermanent =
              !res.data.address_mailing ||
              Object.keys(res.data.address_mailing).length === 0;
            const { x, y } = convertCoordsToPosition(
              129, // x
              isPermanent ? 2221 : 2302, // y
            );
            return makeCheck(x, y);
          })(),
          // ...drawTextLines(res.data.address_mailing?.address_text ?? "", [
          //   { x: 435, y: 2310, maxLength: 45 }, // line 1
          //   { x: 120, y: 2384, maxLength: 70 }, // line 2
          //   { x: 120, y: 2452, maxLength: 100 }, // line 3
          // ]),
          ...drawTextLines(res.data.address_mailing?.address_text ?? "", [
            { x: 435, y: 2310, maxWidth: 220 }, // line 1
            { x: 120, y: 2384, maxWidth: 320 }, // line 2
            { x: 120, y: 2452, maxWidth: 1000 }, // line 3
          ]),
          // ...splitAddressByWord(res.data.address_mailing?.address_text, 40).map(
          //   (line, idx) => {
          //     const coords = idx === 0 ? [435, 2310] : [120, 2385];
          //     return withCoords(...coords, (x, y) => makeText(x, y, line));
          //   },
          // ),
          // withCoords(430, 2307, (x, y) =>
          //   makeText(x, y, res.data.address_mailing?.address_text),
          // ),

          // withCoords(170, 2056, (x, y) =>
          //   makeText(x, y, res.data.address_mailing?.sub_district),
          // ),
          // withCoords(826, 2056, (x, y) =>
          //   makeText(x, y, res.data.address_mailing?.district),
          // ),
          // withCoords(199, 2150, (x, y) =>
          //   makeText(x, y, res.data.address_mailing?.province),
          // ),
          // withCoords(925, 2150, (x, y) =>
          //   makeText(x, y, res.data.address_mailing?.post_code),
          // ),

          // bank
          // withCoords(280, 2623, (x, y) =>
          //   makeText(x, y, res.data.bank?.[0]?.bank_name),
          // ),
          // ...drawTextLines(res.data.bank?.[0]?.bank_name ?? "", [
          //   { x: 350, y: 2615, maxLength: 15 }, // line 1
          //   { x: 120, y: 2654, maxLength: 100 }, // line 2
          // ]),
          ...drawTextLines(res.data.bank?.[0]?.bank_name ?? "", [
            { x: 348, y: 2615, maxWidth: 100 }, // line 1
            { x: 348, y: 2654, maxWidth: 160 }, // line 2
          ]),
          // withCoords(855, 2615, (x, y) =>
          //   makeText(x, y, res.data.bank?.[0]?.branch_name),
          // ),
          ...drawTextLines(res.data.bank?.[0]?.branch_name ?? "", [
            { x: 855, y: 2615, maxWidth: 120 }, // line 1
            { x: 855, y: 2654, maxWidth: 160 }, // line 2
          ]),
          withCoords(450, 2697, (x, y) =>
            makeText(x, y, res.data.bank?.[0]?.account_name),
          ),
          withCoords(580, 2777, (x, y) =>
            makeText(x, y, res.data.bank?.[0]?.account_number),
          ),

          // sponsor
          withCoords(535, 2942, (x, y) =>
            makeText(x, y, res.data.sponsor?.sponsor_name),
          ),
          withCoords(528, 3023, (x, y) =>
            makeText(x, y, res.data.sponsor?.sponsor_code),
          ),
          withCoords(355, 3104, (x, y) =>
            makeText(x, y, res.data.sponsor?.sponsor_mobile),
          ),

          // upline
          (() => {
            const type = res.data.upline?.chart_type;
            if (type !== "L" && type !== "R") return {};
            const isLeft = type === "L";
            const { x, y } = convertCoordsToPosition(isLeft ? 173 : 460, 3264);
            return makeCheck(x, y);
          })(),
          withCoords(535, 3357, (x, y) =>
            makeText(x, y, res.data.upline?.upline_name),
          ),
          withCoords(528, 3427, (x, y) =>
            makeText(x, y, res.data.upline?.upline_code),
          ),
          withCoords(355, 3498, (x, y) =>
            makeText(x, y, res.data.upline?.upline_mobile),
          ),

          // married
          (() => {
            if (!res.data.married || Object.keys(res.data.married).length === 0)
              return [];
            const married = res.data.married;
            const address = res.data.address_married;
            const result = [];
            const isCoupleCode = Object.keys(married).length > 0;
            const { x: checkX, y: checkY } = convertCoordsToPosition(
              isCoupleCode ? 1395 : 1910,
              1816,
            );
            result.push(makeCheck(checkX, checkY));
            result.push(
              withCoords(1270, 645, (x, y) =>
                makeText(x, y, married.id_card || married.passport_id),
              ),
            );
            // const prefixMap = { Mr: 1535, Mrs: 1636, Miss: 1768 };
            // if (prefixMap[married.prefix]) {
            //   const { x, y } = convertCoordsToPosition(
            //     prefixMap[married.prefix],
            //     157,
            //   );
            //   result.push(makeEllipse(x, y));
            // } else {
            //   result.push(
            //     withCoords(2095, 797, (x, y) => makeText(x, y, married.prefix)),
            //   );
            // }

            // const prefixMap = { Mr: 1535, Mrs: 1636, Miss: 1768 };
            // if (prefixMap[married.prefix]) {
            //   const { x, y } = convertCoordsToPosition(
            //     prefixMap[married.prefix],
            //     157,
            //   );
            //   result.push(makeEllipse(x, y));
            // } else {
            //   result.push(
            //     withCoords(2095, 797, (x, y) => makeText(x, y, married.prefix)),
            //   );
            // }
            // Mr: 1312, Mrs: 1481 , Miss: 1656
            const prefixMap = { Mr: 1312, Mrs: 1481, Miss: 1656 };
            if (prefixMap[married.prefix]) {
              const { x, y } = convertCoordsToPosition(
                prefixMap[married.prefix],
                789,
              );
              result.push(makeCheck(x, y)); // เปลี่ยนจาก makeEllipse เป็น makeCheck
            } else {
              const { x: checkX, y: checkY } = convertCoordsToPosition(
                1873,
                789,
              );
              const { x: textX, y: textY } = convertCoordsToPosition(2095, 797);

              result.push(
                makeCheck(checkX, checkY),
                makeText(textX, textY, married.prefix),
              );
            }

            result.push(
              withCoords(1270, 952, (x, y) =>
                makeText(x, y, married.full_name_th),
              ),
              withCoords(1270, 1096, (x, y) =>
                makeText(x, y, married.full_name_en),
              ),
            );
            const birth = dayjs(married.birth_date);
            if (married.birth_date) {
              const married_age = dayjs().diff(birth, "year");
              result.push(
                withCoords(1350, 1248, (x, y) =>
                  makeText(
                    x,
                    y,
                    dayjs(married.birth_date).format("DD / MM / YYYY"),
                  ),
                ),
                withCoords(1800, 1248, (x, y) => makeText(x, y, married_age)),
              );
              // birth
              //   .format("DD")
              //   .split("")
              //   .forEach((char, i) => {
              //     const { x, y } = convertCoordsToPosition(585 + i * 110, 1160);
              //     result.push(makeText(x, y, char, "center"));
              //   });
              // birth
              //   .format("MM")
              //   .split("")
              //   .forEach((char, i) => {
              //     const { x, y } = convertCoordsToPosition(890 + i * 105, 1160);
              //     result.push(makeText(x, y, char, "center"));
              //   });
              // birth
              //   .format("YYYY")
              //   .split("")
              //   .forEach((char, i) => {
              //     const { x, y } = convertCoordsToPosition(
              //       1190 + i * 110,
              //       1160,
              //     );
              //     result.push(makeText(x, y, char, "center"));
              //   });
            }

            result.push(
              withCoords(1595, 1332, (x, y) =>
                makeText(x, y, married.nationality_text),
              ),
              withCoords(1512, 1425, (x, y) => makeText(x, y, married.email)),
              withCoords(1525, 1497, (x, y) => makeText(x, y, married.mobile)),
            );
            // const addrLines = splitAddressByWord(address?.address, 50);
            // result.push(
            //   withCoords(1620, 1500, (x, y) => makeText(x, y, addrLines[0])),
            //   withCoords(1280, 1588, (x, y) => makeText(x, y, addrLines[1])),
            // );
            result.push(
              ...drawTextLines(address?.address_text ?? "", [
                { x: 1905, y: 1578, maxWidth: 115 }, // line 1
                { x: 1270, y: 1660, maxWidth: 320 }, // line 2
                { x: 1270, y: 1740, maxWidth: 1000 }, // line 3
              ]),
            );
            // result.push(
            //   withCoords(1472, 1687, (x, y) =>
            //     makeText(x, y, address?.sub_district),
            //   ),
            //   withCoords(2030, 1687, (x, y) =>
            //     makeText(x, y, address?.district),
            //   ),
            //   withCoords(1423, 1779, (x, y) =>
            //     makeText(x, y, address?.province),
            //   ),
            //   withCoords(2120, 1779, (x, y) =>
            //     makeText(x, y, address?.post_code),
            //   ),
            // );
            return result;
          })(),
        ],
      };

      const pdfBufferPage1 = await new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).getBlob((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsArrayBuffer(blob);
        });
      });

      // 3. โหลด PDF BR02_0002
      const existingPdfBytes = await fetch("/pdf/BR02_0002.pdf").then((res) =>
        res.arrayBuffer(),
      );

      // 4. รวม PDF เข้าด้วยกัน
      const mergedPdf = await PDFDocument.create();
      // นำเข้าหน้าแรก (จาก pdfMake)
      const pdfPage1Doc = await PDFDocument.load(pdfBufferPage1);
      const [page1] = await mergedPdf.copyPages(pdfPage1Doc, [0]);
      mergedPdf.addPage(page1);
      // นำเข้าหน้า 2–5
      const existingDoc = await PDFDocument.load(existingPdfBytes);
      const pages = await mergedPdf.copyPages(
        existingDoc,
        existingDoc.getPageIndices(),
      );
      pages.forEach((p) => mergedPdf.addPage(p));
      // นำเข้าเอกสารที่แนบ
      if (res?.data) {
        await importImgFromDoc(res.data.document_profile, mergedPdf);
        await importImgFromDoc(res.data.document_married, mergedPdf);
      }

      // 5. ดาวน์โหลด
      const finalPdfBytes = await mergedPdf.save();
      const blob = new Blob([finalPdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `ใบสมัคร-${res.data.profile.member_code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating final PDF:", error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <Modal
      title="ดาวน์โหลดเอกสาร"
      visible={open}
      onCancel={handleClose}
      okText="ดาวน์โหลด"
      cancelText="ยกเลิก"
      onOk={_HandleSubmit}
      confirmLoading={loading}
      getContainer={false}
    >
      <Typography.Text>{title}</Typography.Text>
    </Modal>
  );
};

export default DownloadModal;
